// Port of companion/src/plan.mjs at tag js-core-freeze-2026-09-05 (HEAD cb2d250). The same fixture
// and request give the same plan — the same planId (djb2 of the rebuilt request), the same digests,
// the same minted ids, the same keys in the same order — and the same request is refused with the
// same message. structuredClone is deepClone; the frozen `for...of` loops are forEach. The request
// is `unknown` at the boundary, as the frozen module treats it: nothing about it is assumed until
// the checks below have said so, and refusal by name is the contract.
import type {
  Answer,
  AppendableStepTypeId,
  Branch,
  EditContextPlan,
  EditOp,
  EngineId,
  Fixture,
  Plan,
  PlanBase,
  PlanScope,
  PlannedAnswer,
  PlannedRequest,
  PlannedStep,
  RowCounts,
  Step,
  StepAudio,
  WholeBranchPlan,
  WriteStep,
} from '../../dorian-shared/types/episodeScript';
import { deepClone } from './deepClone';
import { findBranch, findStep, sameId } from './model';
import {
  S,
  djb2,
  projectionV1,
  projectionV2,
} from './projection';
import { shapeStepForWrite } from './validate';

/** A JSON object as the boundary sees it: keys and values, nothing assumed. */
type UnknownRecord = Record<string, unknown>;

/** A request that has passed requireObject: an object whose `op` is one of the three planned operations. */
type OpenRequest = UnknownRecord & { op: EditOp };

/** sanitizedStep's output (plan.mjs:289-307): the allowed fields, answers already carrying minted ids. */
interface SanitizedStep {
  stepTypeId: AppendableStepTypeId;
  text?: string;
  characterId?: unknown;
  characterExpressionId?: unknown;
  characterPlacementSide?: unknown;
  answers?: PlannedAnswer[];
}

/** structuralStep's working copy: the sanitized step growing the fields plan.mjs:244-252 add. */
type StructuralDraft = SanitizedStep & {
  id?: string;
  branchId?: EngineId;
  stepAudios?: StepAudio[];
  data?: unknown[];
};

/** basePlan's return (plan.mjs:215-238): every plan starts as an edit-context plan; append_step relabels it. */
type StartingPlan<R extends PlannedRequest> = PlanBase & {
  request: R;
  countsMode: 'edit-context';
  expectedCounts: RowCounts;
  expectedDelta: null;
  needsCanary: false;
};

// The names the planner refuses wherever they appear in a request (plan.mjs:4-20): the filter
// itself, not product text.
/* cspell:disable */
const PROHIBITED_TEXT = [
  /\bai\b/i,
  /\bmodel\b/i,
  /\bopenai\b/i,
  /\bchatgpt\b/i,
  /\bgpt(?:-?\d[\w.-]*)?\b/i,
  /\bclaude\b/i,
  /\banthropic\b/i,
  /\bgemini\b/i,
  /\bllama\b/i,
  /\bazure\b/i,
  /\bmicrosoft\b/i,
  /\bgoogle\b/i,
  /\bamazon\b/i,
  /\baws\b/i,
  /\bdorian\b/i,
];
/* cspell:enable */

// eslint-disable-next-line max-len -- the frozen expression, kept as one literal
const PUBLISH_LIKE = /publish|go[ _-]?live|update[ _-]?live|update[ _-]?in[ _-]?app|in[ _-]?app[ _-]?update|release|deploy|prepublish|submit|restore[ _-]?active|push|save[ _-]?as[ _-]?(?:template|prompt)|share|translate|gamepass/i;
const STRUCTURAL_FORBIDDEN = /delete|remove|reorder|move|insert/i;

// The planner's whole allowed request surface. Anything outside it is refused by
// name: an unknown key is never carried into the plan, and plan.request is
// rebuilt from these fields rather than cloned from the caller's object.
const ALLOWED_REQUEST_KEYS: Record<EditOp, readonly string[]> = {
  set_step_text: ['op', 'branchId', 'stepId', 'text'],
  set_answer_text: ['op', 'branchId', 'stepId', 'answerId', 'text'],
  append_step: ['op', 'branchId', 'step'],
};
// The only extra top-level key: an explicit, allow-listed test-draft scope.
const SCOPE_KEY = 'testDraft';
const ALLOWED_STEP_KEYS = [
  'stepTypeId', 'text', 'characterId', 'characterExpressionId', 'characterPlacementSide', 'answers',
];
const ALLOWED_ANSWER_KEYS = ['text', 'answerPrice', 'gotoBranchId'];
// Only the step types the allowed field set can actually express. A type 11/12/16
// step needs check/switch/action/hub fields, which this schema deliberately omits.
const APPENDABLE_STEP_TYPES: readonly AppendableStepTypeId[] = [1, 2, 3];
const CHOICE_STEP_TYPE = 3;
// The three fields an appended step carries as the request wrote them (plan.mjs:294-296).
const PASSTHROUGH_STEP_KEYS = ['characterId', 'characterExpressionId', 'characterPlacementSide'] as const;

// The Warm Bread canary draft, and nothing else. The offline fixture's own story
// id used to sit beside it, but that is a real story inside the live book rather
// than a test draft, so a request naming it walked straight through the
// allowlist. Offline work names no story at all: it carries the explicit
// sentinel testDraft: { offline: true }, which addresses nothing and therefore
// can never be sent anywhere.
export const WARM_BREAD_TEST_DRAFT_STORY_ID = 475513;

export const TEST_DRAFT_STORY_IDS: readonly number[] = Object.freeze([WARM_BREAD_TEST_DRAFT_STORY_ID]);

const SCOPE_FIELDS = ['storyId', 'offline'];

// Names that address a live story/book/episode rather than a node inside the
// loaded fixture. Compared after stripping separators and lowercasing (the
// spellings below are the normalized forms, for the spell check).
/* cspell:disable */
const LIVE_ID_KEYS = new Set([
  'storyid', 'bookid', 'episodeid', 'chapterid', 'seasonid', 'seriesid',
  'gameid', 'appid', 'titleid', 'sid', 'liveid', 'draftid', 'publicationid',
  'productionid', 'releaseid', 'buildid', 'versionid',
]);
/* cspell:enable */

function fail(message: string): never {
  throw new Error(`Nothing changed. ${message}`);
}

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isAppendableType(value: unknown): value is AppendableStepTypeId {
  return typeof value === 'number' && (APPENDABLE_STEP_TYPES as readonly number[]).includes(value);
}

// BAR-QA-09: the scan runs on a NORMALIZED COPY. A full-width or otherwise
// compatibility-encoded spelling folds to its ASCII form under NFKC, and the
// invisible characters that can be sprinkled between letters are dropped, so
// neither trick walks a prohibited name past the filter. The authored string
// itself is never touched — only this throwaway copy is scanned.
// eslint-disable-next-line max-len, no-misleading-character-class -- the frozen expression: the characters it strips include combining ones
const INVISIBLE = /[\u00AD\u034F\u061C\u115F\u1160\u17B4\u17B5\u180B-\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u206F\u3164\uFE00-\uFE0F\uFEFF\uFFA0]/gu;

function scannable(text: unknown): string {
  return String(text).normalize('NFKC').replace(INVISIBLE, '');
}

// cspell:disable-next-line -- the frozen comment names the shapes the split has to catch
// "gptTokens" and "openai_model" have to read as the words they contain, or a
// prohibited term hides inside a camelCase or snake_case identifier.
function spacedKey(key: string): string {
  // Normalized first, or a compatibility-spelled camelCase key never splits.
  return scannable(key).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_\-.]+/g, ' ');
}

function normalizedKey(key: string): string {
  return key.replace(/[_\-. ]+/g, '').toLowerCase();
}

function prohibited(text: unknown): boolean {
  const subject = scannable(text);
  return PROHIBITED_TEXT.some((pattern) => pattern.test(subject));
}

// Both keys and string values, recursively.
function assertRequestNamesSafe(value: unknown, path = 'request'): void {
  if (typeof value === 'string') {
    if (path !== 'request.op' && prohibited(value)) {
      fail("This change uses a word Basil can't put in the episode.");
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => assertRequestNamesSafe(item, `${path}[${index}]`));
    return;
  }
  if (value && typeof value === 'object') {
    Object.entries(value).forEach(([key, child]) => {
      if (prohibited(spacedKey(key))) {
        fail("This change uses a word Basil can't put in the episode.");
      }
      assertRequestNamesSafe(child, `${path}.${key}`);
    });
  }
}

function assertNoLiveIds(value: unknown, path = 'request'): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) => assertNoLiveIds(item, `${path}[${index}]`));
    return;
  }
  if (value && typeof value === 'object') {
    Object.entries(value).forEach(([key, child]) => {
      if (path === 'request' && key === SCOPE_KEY) return;
      if (LIVE_ID_KEYS.has(normalizedKey(key))) {
        fail("This change points at an episode Basil can't write to.");
      }
      assertNoLiveIds(child, `${path}.${key}`);
    });
  }
}

function readScope(request: UnknownRecord): PlanScope | null {
  if (!Object.prototype.hasOwnProperty.call(request, SCOPE_KEY)) return null;
  const draft = request[SCOPE_KEY];
  if (!isRecord(draft)) {
    fail("Basil couldn't read which episode this change is for.");
  }
  const keys = Object.keys(draft);
  if (keys.length !== 1 || !SCOPE_FIELDS.includes(keys[0])) {
    fail("This change asks for an episode Basil doesn't know.");
  }
  if (keys[0] === 'offline') {
    if (draft.offline !== true) {
      fail("Basil couldn't read how this change was set up.");
    }
    return { offline: true };
  }
  if (!sameId(WARM_BREAD_TEST_DRAFT_STORY_ID, draft.storyId)) {
    fail("This change points at an episode Basil can't write to.");
  }
  return { storyId: Number(draft.storyId) };
}

function assertOnlyKeys(object: UnknownRecord, allowed: readonly string[], path: string): void {
  const subject = path === 'request' ? 'This change' : 'A new line';
  Object.keys(object).forEach((key) => {
    if (!allowed.includes(key)) fail(`${subject} includes something Basil can't send.`);
  });
}

function assertRequestSchema(request: OpenRequest): void {
  assertOnlyKeys(request, [...ALLOWED_REQUEST_KEYS[request.op], SCOPE_KEY], 'request');
  if (request.op !== 'append_step') return;
  const { step } = request;
  if (!isRecord(step)) fail("Basil couldn't read the new line.");
  assertOnlyKeys(step, ALLOWED_STEP_KEYS, 'request.step');
  if (step.answers === undefined) return;
  if (!Array.isArray(step.answers)) fail("Basil couldn't read the answers on that line.");
  step.answers.forEach((answer: unknown, index) => {
    if (!isRecord(answer)) {
      fail("Basil couldn't read one of the answers on that line.");
    }
    assertOnlyKeys(answer, ALLOWED_ANSWER_KEYS, `request.step.answers[${index}]`);
  });
}

function requireObject(request: unknown): asserts request is OpenRequest {
  if (!isRecord(request)) {
    fail("Basil couldn't read this change.");
  }
  if (typeof request.op !== 'string') fail("Basil couldn't tell what this change does.");
  if (PUBLISH_LIKE.test(request.op)) fail('Basil never makes an episode live.');
  if (STRUCTURAL_FORBIDDEN.test(request.op)) fail("Basil can't add, remove or move lines yet.");
  if (!Object.prototype.hasOwnProperty.call(ALLOWED_REQUEST_KEYS, request.op)) {
    fail("Basil doesn't know how to make this change.");
  }
}

// Addendum 1 B law 4: the server hard-caps choice prompts and answers at 40
// characters; everything else is capped at 255.
const CHOICE_TEXT_LIMIT = 40;
const TEXT_LIMIT = 255;

function requireText(text: unknown, field: string, limit = TEXT_LIMIT): asserts text is string {
  if (typeof text !== 'string') fail("Basil couldn't read the words on that line.");
  if (text.length > limit) {
    fail(`This line is ${text.length} characters long, and lines fit up to ${limit}. Trim it and try again.`);
  }
}

// BAR-QA-08: a price is a whole number of coins, never negative, never a
// fraction, never NaN or Infinity. The planner used to accept any `number`, so
// answerPrice -5 was planned as written.
function requirePrice(price: unknown): asserts price is number | undefined {
  if (price === undefined) return;
  if (typeof price !== 'number') fail("That price isn't a number.");
  if (!Number.isFinite(price)) fail(`A price has to be a plain number, and “${String(price)}” isn't one.`);
  if (!Number.isInteger(price)) fail(`A price is a whole number of hearts, so ${price} won't work.`);
  if (price < 0) fail(`A price can't be less than zero, so ${price} won't work.`);
}

function basePlan<R extends PlannedRequest>(fixture: Fixture, planRequest: R, branch: Branch, scope: PlanScope | null): StartingPlan<R> {
  const v1 = projectionV1(fixture);
  const v2 = projectionV2(fixture);
  return {
    planId: `offline-${djb2(S(planRequest)).toString(16).padStart(8, '0')}`,
    mode: 'offline-fixture',
    request: planRequest,
    scope,
    targetBranchId: branch.id,
    beforeDigest: { v1: v1.digest, v2: v2.digest },
    editContextPatch: { nodes: [] },
    // An id-addressed plan is checked by row counts. A whole-branch plan carries
    // no counts at all — "not applicable" is never encoded as three zero rows —
    // and is checked by the structural delta below instead.
    countsMode: 'edit-context',
    expectedCounts: { updatedNodes: 0, updatedSteps: 0, updatedAnswers: 0 },
    expectedDelta: null,
    expectedChangedNodes: [branch.id],
    unexpressedFields: [],
    needsCanary: false,
    warnings: [],
    semantics: { additionalData: 'merge, never replace', network: 'disabled' },
  };
}

function findAnswer(step: Step, id: unknown): Answer | undefined {
  return step.answers?.find((answer) => sameId(answer.id, id));
}

function structuralStep(step: SanitizedStep, branchId: EngineId, fallbackId: string): PlannedStep {
  const result: StructuralDraft = deepClone(step);
  if (result.id === undefined) result.id = fallbackId;
  result.branchId = branchId;
  if (!Array.isArray(result.answers)) result.answers = [];
  if (!Array.isArray(result.stepAudios)) result.stepAudios = [];
  if (!Array.isArray(result.data)) result.data = [];
  // The three character fields are copied as the request wrote them (plan.mjs:294-296): PlannedStep
  // states the schema the planner accepts, and the id and branchId have just been set.
  return result as PlannedStep;
}

// Round 1 (spec F.3): the frozen structuralWriteStep (plan.mjs:254-265) is the per-step half of
// validate.ts's write shape. On every step the pinned plan vectors carry the two agree key for key
// (the extra strips only touch engine fields a step of another type never has). The draft keeps
// its frozen key order and frozen `id`s (a `demo-…` string is never synthetic).
function structuralWriteStep(step: Step): WriteStep {
  return shapeStepForWrite(step) as WriteStep;
}

// Round 1 (spec F.7, J "planner"): an appended answer may route only where the fixture can see —
// a branch it holds, or an id it already routes to (the fixture is a read of part of a story, and
// a route it carries is one the engine accepted). Anything else is a foreign id the engine would
// refuse with a 422 (Addendum 2), so the planner refuses it first, by name.
function knownBranchIds(fixture: Fixture): Set<string> {
  const known = new Set<string>();
  const note = (id: unknown): void => {
    if (id !== null && id !== undefined && id !== '') known.add(String(id));
  };
  fixture.branches.forEach((branch) => {
    note(branch.id);
    note(branch.gotoBranchId);
    (branch.steps ?? []).forEach((step) => {
      (step.answers ?? []).forEach((answer) => note(answer.gotoBranchId));
      (step.switch ?? []).forEach((arm) => note(arm.gotoBranchId));
      note(step.check?.gotoBranchId);
      (step.check?.switch ?? []).forEach((arm) => note(arm.gotoBranchId));
      (step.hubOptions ?? []).forEach((option) => note(option.gotoBranchId));
    });
  });
  return known;
}

function requireRoute(target: unknown, field: string, known: Set<string>): asserts target is EngineId | null | undefined {
  if (target === undefined || target === null) return;
  if (typeof target !== 'number' || !Number.isFinite(target)) fail("Basil couldn't read where that answer goes.");
  if (target < 0) fail("That answer points somewhere Basil can't follow.");
  if (!known.has(String(target))) fail("That answer points at a scene the episode doesn't have.");
}

// Ids are minted by the planner, never taken from the request: the schema has no
// id field for a step that does not exist yet.
function mintStepId(branch: Branch): string {
  const steps = branch.steps ?? [];
  const taken = (candidate: string): boolean => steps.some((step) => sameId(step.id, candidate));
  let ordinal = steps.length + 1;
  let id = `demo-${String(branch.id)}-${ordinal}`;
  while (taken(id)) {
    ordinal += 1;
    id = `demo-${String(branch.id)}-${ordinal}`;
  }
  return id;
}

function sanitizedStep(rawStep: UnknownRecord, branch: Branch, fixture: Fixture): { step: SanitizedStep; stepId: string } {
  if (rawStep.stepTypeId === undefined) fail("Basil couldn't tell what kind of line this is.");
  const { stepTypeId } = rawStep;
  if (!isAppendableType(stepTypeId)) {
    fail('Basil can add a character line, narration or a choice here, and nothing else.');
  }
  const isChoice = stepTypeId === CHOICE_STEP_TYPE;
  if (rawStep.answers !== undefined && !isChoice) {
    fail('Only a choice can carry answers.');
  }
  const step: SanitizedStep = { stepTypeId };
  if (rawStep.text !== undefined) {
    requireText(rawStep.text, 'request.step.text', isChoice ? CHOICE_TEXT_LIMIT : TEXT_LIMIT);
    step.text = rawStep.text;
  }
  PASSTHROUGH_STEP_KEYS.forEach((key) => {
    if (rawStep[key] !== undefined) step[key] = rawStep[key];
  });
  const stepId = mintStepId(branch);
  if (rawStep.answers !== undefined) {
    const known = knownBranchIds(fixture);
    // assertRequestSchema has already refused anything but an array of objects here (plan.mjs:170-177).
    step.answers = (rawStep.answers as UnknownRecord[]).map((answer, index): PlannedAnswer => {
      requireText(answer.text, `request.step.answers[${index}].text`, CHOICE_TEXT_LIMIT);
      requirePrice(answer.answerPrice);
      // Round 1 (F.7): a route is still copied as written (plan.mjs:304), but only a route the
      // fixture can see is planned; a negative, unknown or non-numeric one is refused by name.
      requireRoute(answer.gotoBranchId, `request.step.answers[${index}].gotoBranchId`, known);
      const out: PlannedAnswer = { id: `${stepId}-a${index + 1}`, text: answer.text };
      if (answer.answerPrice !== undefined) out.answerPrice = answer.answerPrice;
      if (answer.gotoBranchId !== undefined) out.gotoBranchId = answer.gotoBranchId;
      return out;
    });
  }
  return { step, stepId };
}

export function createPlan(fixture: Fixture, request: unknown): Plan {
  requireObject(request);
  assertRequestNamesSafe(request);
  const scope = readScope(request);
  assertNoLiveIds(request);
  assertRequestSchema(request);
  const branch = findBranch(fixture, request.branchId);
  if (!branch) fail("That scene isn't in the episode Basil read.");

  if (request.op === 'set_step_text') {
    if (typeof request.text !== 'string') fail("Basil couldn't read the new words for that line.");
    const step = findStep(branch, request.stepId);
    if (!step) fail("That line isn't in the scene any more.");
    requireText(request.text, 'text', step.stepTypeId === CHOICE_STEP_TYPE ? CHOICE_TEXT_LIMIT : TEXT_LIMIT);
    if (step.text === request.text) fail('The episode already says this.');
    const plan: EditContextPlan = {
      ...basePlan(fixture, {
        op: 'set_step_text', branchId: branch.id, stepId: step.id, text: request.text,
      }, branch, scope),
      writePath: 'edit-context',
    };
    plan.editContextPatch.nodes.push({ id: branch.id, steps: [{ id: step.id, text: request.text }] });
    plan.expectedCounts.updatedSteps = 1;
    return plan;
  }

  if (request.op === 'set_answer_text') {
    requireText(request.text, 'text', CHOICE_TEXT_LIMIT);
    const step = findStep(branch, request.stepId);
    if (!step) fail("That line isn't in the scene any more.");
    const answer = findAnswer(step, request.answerId);
    if (!answer) fail("That answer isn't on the line any more.");
    if (answer.text === request.text) fail('The episode already says this.');
    // The answer was found through sameId against the request's id, so this is the id the fixture carries.
    const answerId = answer.id as EngineId;
    const plan: EditContextPlan = {
      ...basePlan(fixture, {
        op: 'set_answer_text', branchId: branch.id, stepId: step.id, answerId, text: request.text,
      }, branch, scope),
      writePath: 'edit-context',
    };
    plan.editContextPatch.nodes.push({
      id: branch.id,
      steps: [{ id: step.id, answers: [{ id: answerId, text: request.text }] }],
    });
    plan.expectedCounts.updatedAnswers = 1;
    return plan;
  }

  if (request.op === 'append_step') {
    // assertRequestSchema has already refused anything but an object here (plan.mjs:168).
    const { step, stepId } = sanitizedStep(request.step as UnknownRecord, branch, fixture);
    const appended = structuralStep(step, branch.id, stepId);
    const base = basePlan(fixture, { op: 'append_step', branchId: branch.id, step: appended }, branch, scope);
    // The frozen plan is the base plan relabelled in place (plan.mjs:358-389); spreading it keeps
    // every key where it was and adds writePath and wholeBranchDraft after semantics, as the
    // assignments did.
    const plan: WholeBranchPlan = {
      ...base,
      // A whole-branch write reports no per-row counts, so the plan carries none
      // and the verifier checks this structural delta instead.
      countsMode: 'whole-branch',
      expectedCounts: null,
      expectedDelta: {
        addedSteps: 1,
        removedSteps: 0,
        changedSteps: 0,
        addedAnswers: appended.answers.length,
        removedAnswers: 0,
      },
      unexpressedFields: [...base.unexpressedFields, { field: 'steps.append', route: 'whole-branch', status: 'needs canary' }],
      needsCanary: true,
      writePath: 'whole-branch',
      wholeBranchDraft: {
        sendable: false,
        reason: "Basil can't add new lines yet, so nothing was sent.",
        branch: {
          id: branch.id,
          title: branch.title,
          description: branch.description,
          gotoBranchId: branch.gotoBranchId,
          locationId: branch.locationId,
          stepTypeMode: branch.stepTypeMode,
          pos: branch.pos,
          steps: [...(branch.steps ?? []), appended].map(structuralWriteStep),
        },
      },
    };
    return plan;
  }

  return fail("Basil doesn't know how to make this change.");
}
